import { Component, h, Host, Element, Watch, Prop, State, EventEmitter, Event, Listen } from '@stencil/core';

import { translations } from './translations';

import { close, alert_exclamation_circle, angle_down } from 'pn-design-assets/pn-assets/icons.js';

import { uuidv4, awaitTopbar, ripple, en, getTotalHeightOffset } from '@/index';
import type { PnLanguages, PnMultiselectOption } from '@/index';

/**
 * The `pn-multiselect` gets its options via javascript.
 *
 * Native HTML does not accept arrays of objects. Most frameworks solves this automatically (Vue, react, etc...),
 * but keep this in mind if you use this component outside of a framework environment.
 *
 * @nativeChange The `pn-multiselect` is built with `input[type=checkbox]` elements, so the `change` event works. However, we recommend the customEvents described above.
 *
 * @see {@link https://portal.postnord.com/web-components/?path=/docs/components-input-multiselect--docs#pn-multiselect%20(options) Options documentation}
 */
@Component({
  tag: 'pn-multiselect',
  styleUrl: 'pn-multiselect.scss',
})
export class PnMultiselect {
  id: string = `pn-multiselect-${uuidv4()}`;
  idLegend: string = `${this.id}-legend`;
  idButton: string = `${this.id}-button`;
  idOptions: string = `${this.id}-options`;
  idHelper: string = `${this.id}-text`;
  idChips: string = `${this.id}-chips`;
  idSr: string = `${this.id}-sr`;

  elGroup: HTMLDivElement;
  elButton: HTMLButtonElement;
  elInput: HTMLInputElement;
  elList: HTMLUListElement;
  elChips: HTMLUListElement;

  movingTimer: NodeJS.Timeout;
  srTimer: NodeJS.Timeout;

  @Element() hostElement: HTMLElement;

  @State() isSearching: boolean = false;

  /** Layout state. */
  @State() open: boolean = false;
  @State() moving: boolean = false;
  @State() upwards: boolean = false;

  @State() srMessage?: string;

  /** Label placed above the select */
  @Prop() label!: string;
  /** Display a helper text underneath the select */
  @Prop() helpertext?: string;
  /**
   * This is what will be shown on load if no value is used.
   * The `placeholder` will override the default text used if you have the `search` prop active.
   */
  @Prop() placeholder?: string;
  /** HTML name of the checkbox elements. Used for each checkbox inside the multiselect. */
  @Prop() name?: string;
  /** Select HTML id */
  @Prop() selectId: string = this.id;
  /** HTML name of the input element.*/
  @Prop() selectName?: string;
  /** Display an icon to the left of the select input */
  @Prop() icon?: string;
  /** Manually set the language, not needed if you have the pnTopbar available */
  @Prop() language?: PnLanguages = null;
  /**
   * Array of options.
   * @hide true
   **/
  @Prop({ mutable: true }) options!: PnMultiselectOption[];

  /**
   * Adds a "Select all" option into the list.
   * If you use the search feature at the same time, clicking this option will only toggle the options found.
   * @see {@link search}
   * @category Features
   **/
  @Prop() selectAll: boolean = false;
  /**
   * Set a custom value for the "Select all value" option.
   * @see {@link selectAll}
   * @category Features
   */
  @Prop() allValue?: string = 'pn_multiselect_all';
  /**
   * Allow the user to search among the options.
   * The selected options will now display underneath the multiselect element.
   *
   * @category Features
   **/
  @Prop() search?: boolean = false;
  /** Set the search query of the multiselect.
   *
   * @see {@link search}
   * @category Features
   */
  @Prop({ mutable: true }) searchQuery?: string = '';
  /**
   * Decide how many items should be shown before ellipsis. Requires the `search` prop to work.
   *
   * @see {@link search}
   * @category Features
   **/
  @Prop() itemCount: number = 5;
  /**
   * Use a custom max-height for the dropdown list.
   * @description The component will automatically set the max height of the dropdown list on its own.
   * It takes the `pn-topbar` into account and will open in the direction that fits best.
   * Use this prop to override this behaviour and use a custom max-height.
   * @category Features
   **/
  @Prop() maxHeight?: string;
  /**
   * Force the dropdown to always open upwards.
   * @category Features
   **/
  @Prop() top?: boolean = false;
  /**
   * Force the dropdown to always open downwards.
   * @category Features
   **/
  @Prop() bottom?: boolean = false;

  /** Set the select as required. @category Validation */
  @Prop() required?: boolean = false;
  /** Disable the select. @category Validation */
  @Prop() disabled?: boolean = false;
  /** Trigger the invalid state. @category Validation */
  @Prop() invalid?: boolean = false;
  /** Display an error message and trigger the invalid state. @category Validation */
  @Prop() error?: string;

  @Watch('open')
  handleOpen() {
    this.handleMoving();

    if (this.open) {
      this.handleDirection();
      this.handleMaxHeight();
      this.handleWidth();
    }
  }

  @Watch('search')
  handleSearch() {
    if (!this.search)
      this.options = [
        ...this.options.map(option => {
          const item = {
            ...option,
            hide: false,
          };

          if (item?.group?.length) item.group.map(opt => ({ ...opt, hide: false }));

          return item;
        }),
      ];
  }

  @Watch('searchQuery')
  handleSearchQuery() {
    if (this.search && typeof this.searchQuery === 'string') this.handleInputSearch();
  }

  @Watch('selectId')
  handleSelectId() {
    const id = this.selectId;
    this.idLegend = `${id}-legend`;
    this.idButton = `${id}-button`;
    this.idOptions = `${id}-options`;
    this.idHelper = `${id}-text`;
    this.idChips = `${id}-chips`;
    this.idSr = `${id}-sr`;
  }

  /**
   * If the select is open and you resize the window.
   * Remove all css props and disable the animations entierly.
   **/
  @Listen('resize', { target: 'window' })
  handleResize({ type }: Event) {
    if (!this.open) return;
    this.toggleOpen(false);

    this.moving = type === 'resize';

    this.elList.style.removeProperty('--pn-select-options-width');
    this.elList.style.removeProperty('--pn-select-max-height');
  }

  /** Dispatched everytime the multiselect is opened or closed. */
  @Event() toggleSelect: EventEmitter<{ open: boolean }>;
  /** This event contains the entire options array with the new props. Dispatched everytime you make changes to any option. */
  @Event() allOptions: EventEmitter<PnMultiselectOption[]>;
  /** Dispatched when you toggle an option. Includes all the props of the option. */
  @Event() selectedOption: EventEmitter<PnMultiselectOption>;
  /**
   * This event is dispatched when the user toggles the "Choose all options" box.
   * Also triggers when you click the "Select {number} options" if you are performing a search at the same time.
   **/
  @Event() selectedAllOptions: EventEmitter<{
    checked: boolean;
    searching: boolean;
    query?: string;
    found?: PnMultiselectOption[];
  }>;
  /**
   * Contains the search text and the options found for that query.
   * Dispacthed everytime you change the search query.
   **/
  @Event() searchInput: EventEmitter<{
    query: string;
    found?: PnMultiselectOption[];
  }>;

  async componentWillLoad() {
    this.handleSelectId();
    if (this.language === null) await awaitTopbar(this.hostElement);
  }

  componentDidLoad() {
    if (!!this.searchQuery?.length) this.handleSearchQuery();
  }

  private emitEvents(option?: PnMultiselectOption, all?: boolean) {
    this.allOptions.emit(this.options);

    if (option) return this.selectedOption.emit(option);

    const data: { checked: boolean; searching: boolean; query?: string; found?: PnMultiselectOption[] } = {
      checked: all,
      searching: this.isSearching,
    };

    if (this.search && this.optionsTotal() !== this.optionsSearch().length) {
      data.query = this.searchQuery;
      data.found = this.optionsSearch();
    }

    this.selectedAllOptions.emit(data);
  }

  private optionsChecked(): PnMultiselectOption[] {
    const list = this.options?.reduce((sum, item) => {
      const subgroup = item.group ? [...item.group.filter(({ checked }) => checked)] : [];

      if (item.checked) sum.push(item);

      if (subgroup?.length) sum.push(...subgroup);

      return sum;
    }, []);

    return list;
  }

  private optionsTotal(): number {
    return this.options?.reduce((sum, item) => sum + (item?.group?.length ? item.group.length + 1 : 1), 0);
  }

  private optionsIndex(val: string): { indexGroup: number; indexNested?: number } {
    const findNested = ({ group = [] }: PnMultiselectOption) => group?.findIndex(({ value }) => value === val);

    const indexGroup = this.options.findIndex(option => option.value === val || findNested(option) !== -1);
    const indexNested = findNested(this.options?.[indexGroup]);

    return {
      indexGroup,
      indexNested,
    };
  }

  private optionsCheckedTotal(): number {
    return this.optionsChecked()?.length;
  }

  private optionsCheckedPreview(): PnMultiselectOption[] {
    return this.optionsChecked()?.slice(0, this.itemCount);
  }

  private optionsCheckedLabels(): string {
    return this.optionsChecked()
      ?.map(({ label }) => label)
      ?.join(', ');
  }

  private optionsSearch(): PnMultiselectOption[] {
    const list = this.options?.reduce((sum, item) => {
      const subgroup = item.group ? [...item.group.filter(({ hide }) => !hide)] : [];
      if (!item.hide) sum.push(item);
      if (subgroup?.length) sum.push(...subgroup);
      return sum;
    }, []);

    return list;
  }

  private noResults() {
    return this.optionsSearch()?.length === 0;
  }

  private isIndeterminate(groupIndex: number): boolean {
    const option = this.options?.[groupIndex];
    if (option?.group?.length) {
      const all = option.group.every(({ checked }) => checked);
      const none = option.group.every(({ checked }) => !checked);

      return !(all || none);
    }
    return false;
  }

  private optionSelect({ val, checked, chip }: { val: string; checked: boolean; chip?: number }) {
    if (val === this.allValue) return this.optionSelectAll(checked);

    const options = this.options;

    const { indexGroup, indexNested } = this.optionsIndex(val);

    const group = options[indexGroup]?.group;
    const nested = group?.[indexNested];

    if (nested?.value) {
      options[indexGroup].group[indexNested].checked = checked;

      const allSiblingsChecked = group?.every(({ checked }) => checked);

      if (allSiblingsChecked) options[indexGroup].checked = true;
      else options[indexGroup].checked = false;
    } else {
      options[indexGroup].checked = checked;
      if (group?.length) options[indexGroup].group = group?.map(opt => ({ ...opt, checked: checked }));
    }

    const option = indexNested === -1 ? options[indexGroup] : options[indexGroup].group[indexNested];

    this.emitEvents(option);

    this.options = [...options];

    if (typeof chip === 'number') {
      this.handleSrMesssage(`${this.translate('REMOVED')} ${option.label}`);

      requestAnimationFrame(() => {
        const index = chip === this.optionsCheckedTotal() ? chip - 1 : chip;
        const btn = Array.from(this.elChips.children)?.[index];
        btn?.querySelector('button').focus({ preventScroll: true });
      });
    }
  }

  private optionSelectAll(checked: boolean) {
    this.options = [
      ...this.options.map(option => {
        const opt = {
          ...option,
          checked: this.isSearching ? (option.hide ? option.checked : checked) : checked,
        };

        if (opt.group)
          opt.group = [
            ...opt.group.map(item => ({
              ...item,
              checked: this.isSearching ? (item.hide ? item.checked : checked) : checked,
            })),
          ];

        const allChecked = opt?.group?.every(({ checked }) => checked);
        if (opt?.group?.length) opt.checked = allChecked;

        return opt;
      }),
    ];

    this.emitEvents(null, checked);
  }

  private optionSelected(val: string): boolean {
    return !!this.optionsChecked()?.find(({ value, checked }) => value === val && checked);
  }

  private translate(prop: string): string {
    const text = translations?.[prop]?.[this.language || en];
    if (text.includes('{number}')) return text.replace('{number}', this.optionsSearch().length);
    return text;
  }

  private getRect(element: HTMLElement): DOMRect {
    return element?.getBoundingClientRect();
  }

  private ripple({ clientX, clientY, target }: MouseEvent) {
    const isKeyboard = clientX === 0 && clientY === 0;
    const element = (target as HTMLInputElement).nextElementSibling;
    const { x, width, y, top } = this.getRect(element as HTMLElement);
    const clientCor = isKeyboard ? { clientX: x + width - 24, clientY: y - top } : { clientX, clientY };

    ripple(clientCor, element as HTMLElement);
  }

  private handleMoving() {
    this.moving = true;
    clearTimeout(this.movingTimer);
    this.movingTimer = setTimeout(() => (this.moving = false), 200);
  }

  private handleSrMesssage(text: string) {
    this.srMessage = text;
    clearTimeout(this.srTimer);
    this.srTimer = setTimeout(() => (this.srMessage = null), 2000);
  }

  private handleDirection() {
    if (this.top) return (this.upwards = true);
    if (this.bottom) return (this.upwards = false);

    const { bottom, top } = this.getRect(this.elInput);

    const oneEm = 16;

    /*
     * Take the inner window height and subtract the elements bottom and 1em (16px)
     * If the max height of this calculation is less than the space above the element, open it upwards.
     * Do another calculation with the top value - 1em.
     */
    const offsetTop = window.innerHeight - bottom - oneEm;
    const offsetBottom = top - oneEm;

    /** Always point downwards, unless the space to the bottom is less than half of the top offset space. */
    this.upwards = offsetBottom / 2 > offsetTop;
  }

  private handleMaxHeight() {
    if (this.maxHeight) return this.elGroup.style.setProperty('--pn-select-max-height', this.maxHeight);

    const { bottom, top } = this.getRect(this.elInput);
    const inputHeight = this.upwards ? top : bottom;

    const offsetTop = getTotalHeightOffset();

    const offset = this.upwards ? offsetTop + 16 : 16;

    const maxHeight = this.upwards ? inputHeight - offset : window.innerHeight - inputHeight - offset;

    this.elGroup.style.setProperty('--pn-select-max-height', `${Math.ceil(maxHeight)}px`);
  }

  private handleWidth() {
    const { width } = this.getRect(this.elInput);
    this.elGroup.style.setProperty('--pn-select-options-width', `${Math.ceil(width)}px`);
  }

  private setFocus() {
    if (this.search) this.elInput.focus({ preventScroll: true });
    else this.elButton.focus({ preventScroll: true });
  }

  private hasError(): boolean {
    return this.invalid || !!this.error;
  }

  private hasMessage(): boolean {
    return !!this.helpertext || !!this.error;
  }

  private toggleOpen(state?: boolean) {
    this.open = state ?? !this.open;
    this.toggleSelect.emit({ open: this.open });
  }

  private handleInputKeyboard(event: KeyboardEvent) {
    const { code } = event;

    if (code === 'Escape') return this.toggleOpen(false);
    if (!this.open && code.match(/^(Space|Enter)$|Arrow|^Key.*$/)) this.toggleOpen();
  }

  private setSearchQuery(value: string) {
    this.searchQuery = value;
  }

  private handleInputSearch() {
    if (this.isEmpty()) return;

    const term = this.searchQuery;
    const performSearch = ({ label, helpertext, value }: PnMultiselectOption): boolean => {
      return `${label} ${helpertext} ${value}`.trim().toLowerCase().includes(term.toLowerCase());
    };

    const options = this.options.map(option => {
      const foundNested = option?.group?.map(item => ({ ...item, hide: !performSearch(item) })) || [];

      const found = performSearch(option) || foundNested?.some(({ hide }) => !hide);

      option.hide = !found;
      if (option?.group?.length) option.group = [...foundNested];

      return option;
    });

    this.isSearching = term !== '';

    this.options = [...options];

    this.searchInput.emit({ query: term, found: this.optionsSearch() });
  }

  private getListItem(index: number): HTMLInputElement {
    const { value } = this.options[index];
    return this.elList.querySelector<HTMLInputElement>(`.pn-multiselect-option-input[value='${value}']`);
  }

  private getOptionIndex(val: string, triggers: (boolean | number)[], code?: string) {
    const total = this.optionsTotal() - 1;

    if (code === 'End') return total;
    if (code === 'Home') return 0;

    const valIndex = this.options.findIndex(({ value }) => value === val);

    const count = triggers.find(item => typeof item === 'number') as number;
    const index = valIndex + count;

    if (index >= total) return total;
    if (index <= 0) return 0;

    return index;
  }

  private checkboxNav(e: KeyboardEvent, val: string) {
    const { code } = e;

    const arrowUp = code === 'ArrowUp' && -1;
    const arrowDown = code === 'ArrowDown' && 1;
    const pageUp = code === 'PageUp' && -10;
    const pageDown = code === 'PageDown' && 10;
    const home = code === 'Home' && 0;
    const end = code === 'End' && this.optionsTotal() - 1;
    const tab = code === 'Tab';
    const space = code === 'Space';
    const enter = code === 'Enter';
    const escape = code === 'Escape';

    const triggers = [arrowUp, arrowDown, pageUp, pageDown, home, end, tab, space, enter, escape];

    if (!triggers.some(item => typeof item === 'number' || item)) return;

    if (tab || space || enter) return;

    if (escape) {
      this.setFocus();
      return this.toggleOpen(false);
    }

    const item = this.getOptionIndex(val, triggers, code);

    e.preventDefault();

    return this.getListItem(item)?.focus();
  }

  private handleBlur(event: FocusEvent) {
    const target = event.relatedTarget as HTMLDivElement;

    const currentSelect = target?.closest<HTMLPnMultiselectElement>('pn-multiselect');
    const insideCurrent = currentSelect?.selectId === this.selectId;
    const jumpingToChips = (event.relatedTarget as HTMLElement)?.className === 'pn-multiselect-chip-button';

    if (!insideCurrent || jumpingToChips) this.toggleOpen(false);
  }

  private handleLabel() {
    if (this.disabled) return;
    this.setFocus();
  }

  /** Check if there are no options in the list. */
  private isEmpty(): boolean {
    return (this.options?.length || 0) === 0;
  }

  private showSelectAll(): boolean {
    return this.selectAll && !this.isEmpty() && !this.noResults();
  }

  /** Check if the empty/nothing found should be visible. */
  private showEmptyOption() {
    return (this.isSearching && this.noResults()) || this.isEmpty();
  }

  private getPlaceholder(): string {
    const autoTranslation = this.search ? 'SEARCH' : 'SELECT_AN_OPTION';
    return this.placeholder || this.translate(autoTranslation);
  }

  private handleChange({ target }: InputEvent) {
    const { value, checked } = target as HTMLInputElement;
    this.optionSelect({ val: value, checked });
  }

  /** Display the "X more selected options" text. */
  private additonalOptions() {
    const count = this.optionsCheckedTotal() - this.itemCount;
    const single = `MORE_OPTION${count === 1 ? '' : 'S'}`;
    return `${count} ${this.translate(single)}`;
  }

  private describedBy(): string {
    const list = [this.search && this.idChips, this.srMessage && this.idSr, !!this.helpertext && this.idHelper].filter(
      Boolean,
    );
    if (list.length) return list.join(' ');
    return null;
  }

  private renderOption(
    {
      label,
      value,
      checked,
      helpertext,
      icon,
      id = `${this.selectId}-${value || label}`,
      invalid,
      disabled,
      hide,
      group,
    }: PnMultiselectOption,
    indexGroup?: number,
    indexNested?: number,
  ): HTMLLIElement {
    return (
      <li class="pn-multiselect-option" key={id} hidden={hide}>
        <input
          type="checkbox"
          id={id}
          class="pn-multiselect-option-input"
          checked={checked ?? this.optionSelected(value)}
          indeterminate={indexNested === undefined && this.isIndeterminate(indexGroup)}
          name={this.name}
          value={value}
          disabled={this.disabled || disabled}
          required={this.required}
          aria-invalid={invalid ? 'true' : null}
          aria-describedby={helpertext ? `${id}-helper` : null}
          onClick={(event: MouseEvent) => this.ripple(event)}
          onKeyDown={(event: KeyboardEvent) => this.checkboxNav(event, value)}
          onBlur={(event: FocusEvent) => this.handleBlur(event)}
        />
        <div class="pn-multiselect-option-content">
          {!!icon && <pn-icon icon={icon} color="blue900" />}
          <div class="pn-multiselect-option-text">
            <label class="pn-multiselect-option-label" htmlFor={id}>
              <span>{label}</span>
            </label>
            {helpertext && (
              <p class="pn-multiselect-option-helper" id={`${id}-helper`}>
                {helpertext}
              </p>
            )}
          </div>
          <div class="pn-multiselect-option-checkbox">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
              <polyline
                class="pn-multiselect-option-checkbox-checkmark-path"
                points="4,12 9,17 20,6"
                stroke-width="3"
              />
              <polyline
                class="pn-multiselect-option-checkbox-indeterminate-path"
                points="4,12 20,12"
                stroke-width="3"
              />
            </svg>
          </div>
        </div>
        {group?.length && (
          <ul class="pn-multiselect-optgroup">{group?.map((option, i) => this.renderOption(option, indexGroup, i))}</ul>
        )}
      </li>
    );
  }

  private renderOptions(): HTMLUListElement {
    return (
      <ul
        id={this.idOptions}
        class="pn-multiselect-options"
        data-open={this.open}
        data-upwards={this.upwards}
        data-moving={this.moving}
        onChange={(event: InputEvent) => this.handleChange(event)}
        ref={el => (this.elList = el)}
      >
        {this.showSelectAll() &&
          this.renderOption({
            label: this.translate(`SELECT_${this.isSearching ? 'FOUND' : 'ALL'}_OPTIONS`),
            value: this.allValue,
            checked: this.optionsTotal() === this.optionsCheckedTotal(),
          })}

        {this.options?.map((option, index) => this.renderOption(option, index))}

        {this.showEmptyOption() && (
          <li class="pn-multiselect-option" role="alert">
            <div class="pn-multiselect-option-content">
              <div class="pn-multiselect-option-text">
                <span class="pn-multiselect-option-label">
                  {this.translate(this.isEmpty() ? 'NO_OPTIONS' : 'NO_SEARCH_RESULTS')}
                </span>
              </div>
            </div>
          </li>
        )}
      </ul>
    );
  }

  private renderChips(): HTMLUListElement {
    return (
      <ul
        id={this.idChips}
        class="pn-multiselect-chips"
        aria-label={this.translate('SELECTED_OPTIONS')}
        ref={el => (this.elChips = el)}
      >
        {this.optionsCheckedPreview()?.map(({ label, value }, index) => (
          <li
            class="pn-multiselect-chip"
            aria-setsize={this.optionsCheckedTotal()}
            aria-posinset={index + 1}
            key={`${label}-${value}`}
          >
            <span class="pn-multiselect-chip-label">{label}</span>
            <button
              type="button"
              class="pn-multiselect-chip-button"
              aria-label={`${this.translate('REMOVE')} ${label}`}
              onClick={() => this.optionSelect({ val: value, checked: false, chip: index })}
            >
              <pn-icon icon={close} small color="blue700" />
            </button>
          </li>
        ))}
        {this.optionsCheckedTotal() > this.itemCount && (
          <li class="pn-multiselect-chip" data-count>
            <span class="pn-multiselect-chip-label">+ {this.additonalOptions()}</span>
          </li>
        )}
      </ul>
    );
  }

  render() {
    return (
      <Host>
        <fieldset class="pn-multiselect" data-icon={!!this.icon} data-error={this.hasError()} disabled={this.disabled}>
          {this.label && (
            <legend id={this.idLegend} class="pn-multiselect-label" onClick={() => this.handleLabel()}>
              <span>{this.label}</span>

              {!!this.optionsChecked()?.length && (
                <span>
                  {this.optionsCheckedTotal()}/{this.optionsTotal()}
                </span>
              )}
            </legend>
          )}
          <div class="pn-multiselect-group" ref={el => (this.elGroup = el)}>
            <div class="pn-multiselect-input">
              {!!this.icon && <pn-icon class="pn-multiselect-icon" data-custom icon={this.icon} />}

              <input
                tabindex={this.search ? null : '-1'}
                type={this.search ? 'search' : 'input'}
                id={this.selectId}
                class="pn-multiselect-element"
                value={this.search ? this.searchQuery : this.optionsCheckedLabels()}
                name={this.selectName}
                placeholder={this.getPlaceholder()}
                required={this.search ? null : this.required}
                aria-labelledby={this.search ? this.idLegend : this.idButton}
                aria-describedby={this.describedBy()}
                aria-controls={`${this.idOptions} ${this.search ? this.idChips : ''}`}
                aria-invalid={(!!this.error || this.invalid).toString()}
                disabled={this.disabled}
                readonly={!this.search}
                onClick={() => this.toggleOpen()}
                onKeyDown={e => this.handleInputKeyboard(e)}
                onBlur={e => this.handleBlur(e)}
                onInput={e => this.search && this.setSearchQuery((e.target as HTMLInputElement).value)}
                ref={el => (this.elInput = el)}
              />

              {this.hasError() && (
                <pn-icon class="pn-multiselect-icon" data-error icon={alert_exclamation_circle} color="warning" />
              )}

              <button
                id={this.idButton}
                type="button"
                class="pn-multiselect-button"
                aria-label={this.translate(`BUTTON_${this.open ? 'CLOSE' : 'OPEN'}`)}
                aria-describedby={this.search ? null : this.selectId}
                aria-controls={this.idOptions}
                aria-expanded={this.open.toString()}
                onClick={() => this.toggleOpen()}
                ref={el => (this.elButton = el)}
              >
                <pn-icon class="pn-multiselect-icon" icon={angle_down} color="blue700" />
              </button>
            </div>

            {this.renderOptions()}
          </div>

          {this.hasMessage() && (
            <p id={this.idHelper} class="pn-multiselect-description" role={!!this.error ? 'alert' : null}>
              <span>{this.error || this.helpertext}</span>
            </p>
          )}

          {this.search && this.renderChips()}

          <slot />

          {this.search && (
            <p id={this.idSr} class="pn-multiselect-sr-only" role="alert" aria-live="assertive">
              {this.srMessage && <span>{this.srMessage}</span>}
            </p>
          )}
        </fieldset>
      </Host>
    );
  }
}
